import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, Star, X } from "lucide-react";
import Panel from "../ui/Panel/Panel.jsx";
import Button from "../ui/Button/Button.jsx";
import Checkbox from "../forms/Checkbox/Checkbox.jsx";
import FormField from "../forms/FormField/FormField.jsx";
import TextInput from "../forms/TextInput/TextInput.jsx";
import ShareBox from "../share/ShareBox.jsx";
import { dismissPrompt, getReferralSummary, submitFeedback } from "../../services/engagementService";
import { track } from "../../seo/track";
import { money } from "../../utils/format.js";

// Must match FeedbackService.TESTIMONIAL_CONSENT_VERSION in form2290-api: the API records
// which wording the filer signed. Change both together, with a new version date.
export const TESTIMONIAL_CONSENT_VERSION = "2026-10-08";
export const TESTIMONIAL_CONSENT_TEXT =
  "I give EasyFile2290 (operated by Quantiva LLC) permission to quote my comment above, word for word, " +
  "with the name, role and location I enter here, on easyfile2290.com and its social media pages. " +
  "I was not paid or given anything for this. I can withdraw this permission at any time in Settings, " +
  "and EasyFile2290 will then stop using the quote and remove it from its website.";

const ROLES = ["Owner-operator", "Fleet owner", "Fleet manager", "Tax preparer", "Dispatcher", "Other"];
const STAR_LABELS = ["Poor", "Fair", "Good", "Very good", "Excellent"];

const textareaCls =
  "w-full min-h-[90px] border-[1.5px] border-[var(--line-2)] rounded-[var(--r-sm)] px-3 py-2 bg-[var(--panel)] text-[var(--ink)] text-[15px] font-body outline-none focus:border-[var(--accent)]";

/**
 * Star rating for ONE IRS-accepted return, shown after the filer downloads its stamped
 * Schedule 1 (My Filings) or from the dashboard card. Ratings go to the owner only - there is
 * no public review site (owner decision 2026-10-08), so nothing here asks for a public review.
 * Kept to one question - stars plus an optional comment; groups, the follow-up email and the
 * testimonial permission sit behind "More options" and go in the same Send.
 * "Not now" stops the prompt for this return for good (RATE_RETURN dismissal); the ✕ only closes
 * it for this visit. Either one, with a star picked or anything typed, first asks whether to send
 * it - nothing is saved until Send, and a filer who wrote a comment then closed lost it silently.
 * Rendered into document.body (portal): opened from inside a dashboard Panel, a fixed overlay
 * would otherwise be clipped to that card.
 */
export default function FeedbackPanel({ open, returnId, businessName, refCode, onClose }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [groups, setGroups] = useState("");
  const [followUp, setFollowUp] = useState(false);
  const [consent, setConsent] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");
  const [signature, setSignature] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState(false);
  const [more, setMore] = useState(false);
  // "x" | "notNow" while asking "Send your feedback before closing?", else null.
  const [confirmClose, setConfirmClose] = useState(null);
  // Loaded once the rating is sent, for the share box's credit amount and yearly limit (from
  // the server, as on the dashboard). If it can't be loaded, or the filer has already earned
  // this period's limit, the share box keeps its plain wording.
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    if (!done) return undefined;
    let active = true;
    getReferralSummary()
      .then((s) => {
        if (active) setReferral(s || null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [done]);

  if (!open) return null;

  const canEarnCredit =
    !!refCode && !!referral?.creditValue && referral.earnedThisTaxPeriod < referral.capPerTaxPeriod;

  const hasInput = rating > 0 || !!comment.trim() || !!groups.trim() || followUp || consent;

  const close = (kind) => {
    if (kind === "notNow") dismissPrompt("RATE_RETURN", returnId).catch(() => {});
    onClose({ submitted: false });
  };

  const requestClose = (kind) => {
    if (hasInput) setConfirmClose(kind);
    else close(kind);
  };

  const validate = () => {
    const e = {};
    if (!rating) e.rating = "Choose a star rating.";
    if (consent) {
      if (!comment.trim()) e.comment = "Write a comment for us to quote, or untick the permission box.";
      if (!displayName.trim()) e.displayName = "Enter the name to show with your quote.";
      if (!signature.trim()) e.signature = "Type your full name to sign your permission.";
    }
    setErrors(e);
    // Testimonial fields live behind "More options" - open it so their errors can be seen.
    if (e.displayName || e.signature) setMore(true);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    setConfirmClose(null);
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");
    try {
      await submitFeedback({
        returnId,
        rating,
        comment: comment.trim() || null,
        followUpRequested: followUp,
        groups: groups.trim() || null,
        testimonialConsent: consent,
        testimonialDisplayName: consent ? displayName.trim() : null,
        testimonialRole: consent ? role || null : null,
        testimonialLocation: consent ? location.trim() || null : null,
        testimonialSignature: consent ? signature.trim() : null,
      });
      track("feedback_submit", { rating, testimonial: consent });
      setDone(true);
    } catch (err) {
      setServerError(err.message || "We couldn't save your feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-[rgba(15,23,42,0.55)] backdrop-blur-[2px] flex items-start sm:items-center justify-center z-[200] p-4 overflow-y-auto"
      onClick={() => (done ? onClose({ submitted: true }) : null)}
    >
      <Panel padded className="w-full max-w-[560px] !p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label="Close"
          onClick={() => (done ? onClose({ submitted: true }) : confirmClose ? null : requestClose("x"))}
          className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--ink)] bg-transparent border-0 cursor-pointer p-1"
        >
          <X size={18} />
        </button>

        {done ? (
          <>
            <h3 className="font-disp text-xl mb-1">Thanks - we read every comment.</h3>
            <p className="text-[14px] text-[var(--muted)] mb-4">
              {followUp ? "We'll email you about your comment. " : ""}
              {consent ? "You can withdraw your quote permission anytime in Settings." : ""}
            </p>
            {canEarnCredit ? (
              <ShareBox
                refCode={refCode}
                placement="after-rating"
                title={`Get ${money(referral.creditValue)} off your next filing`}
                intro={`Share your link. When a friend's first return is accepted by the IRS, you get a ${money(
                  referral.creditValue
                )} credit - the service fee for one truck. Up to ${referral.capPerTaxPeriod} a year, and they stack.`}
              />
            ) : (
              <ShareBox refCode={refCode} placement="after-rating" />
            )}
            <div className="flex justify-end mt-4">
              <Button onClick={() => onClose({ submitted: true })}>Done</Button>
            </div>
          </>
        ) : confirmClose ? (
          <>
            <h3 className="font-disp text-xl mb-1">Send your feedback before closing?</h3>
            <p className="text-[14px] text-[var(--muted)] mb-4">
              Your feedback isn't saved until you send it.
            </p>
            <div className="flex justify-between gap-3 mt-2">
              <Button variant="ghost" onClick={() => close(confirmClose)} disabled={submitting}>
                Discard
              </Button>
              <Button onClick={submit} disabled={submitting}>
                {submitting ? "Sending…" : "Send feedback"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h3 className="font-disp text-xl mb-1">How was filing with EasyFile2290?</h3>
            <p className="text-[13px] text-[var(--muted)] mb-4">
              {businessName ? <>Your Form 2290 for <b>{businessName}</b> was accepted by the IRS. </> : null}
              Your rating goes straight to our team.
            </p>

            <div className="flex items-center gap-1 mb-1" role="radiogroup" aria-label="Star rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n} star${n > 1 ? "s" : ""} - ${STAR_LABELS[n - 1]}`}
                  onClick={() => {
                    setRating(n);
                    setErrors((p) => ({ ...p, rating: "" }));
                  }}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className="bg-transparent border-0 p-1 cursor-pointer"
                >
                  <Star
                    size={30}
                    className={(hover || rating) >= n ? "text-[#f59e0b]" : "text-[var(--line-2)]"}
                    fill={(hover || rating) >= n ? "currentColor" : "none"}
                  />
                </button>
              ))}
              <span className="text-[13px] text-[var(--muted)] ml-2">{STAR_LABELS[(hover || rating) - 1] || ""}</span>
            </div>
            {errors.rating && <div className="text-xs text-[var(--danger)] mb-2">{errors.rating}</div>}

            <FormField label="Anything we could do better? (optional)" error={errors.comment} showError={!!errors.comment}>
              <textarea
                className={textareaCls}
                maxLength={1000}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What worked, what didn't, what should we add?"
              />
            </FormField>

            <button
              type="button"
              onClick={() => setMore((m) => !m)}
              aria-expanded={more}
              className="flex items-center gap-1 bg-transparent border-0 p-0 mb-3 cursor-pointer text-[13px] font-semibold text-[var(--accent)]"
            >
              More options {more ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>

            {more && (
              <>
                <FormField
                  label="Which trucking groups or associations are you in? (optional)"
                  hint="For example a state trucking association, OOIDA, or a Facebook group. Helps us show up where truckers are."
                >
                  <TextInput maxLength={300} value={groups} onChange={(e) => setGroups(e.target.value)} />
                </FormField>

                <Checkbox
                  id="feedbackFollowUp"
                  title="Email me about my comment"
                  checked={followUp}
                  onChange={(e) => setFollowUp(e.target.checked)}
                />

                <Checkbox
                  id="feedbackTestimonial"
                  title="EasyFile2290 may quote my comment on its website and social pages"
                  subtitle="Optional. Nothing is given for this."
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />

                {consent && (
                  <div className="border border-[var(--line)] rounded-[var(--r-sm)] p-4 mb-3 bg-[var(--panel-2)]">
                    <div className="grid gap-x-3 sm:grid-cols-2">
                      <FormField label="Name to show" required error={errors.displayName} showError={!!errors.displayName} hint="e.g. Mike R.">
                        <TextInput maxLength={60} value={displayName} onChange={(e) => setDisplayName(e.target.value)} invalid={!!errors.displayName} />
                      </FormField>
                      <FormField label="Role (optional)">
                        <select
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                          className="w-full h-11 border-[1.5px] border-[var(--line-2)] rounded-[var(--r-sm)] px-3 bg-[var(--panel)] text-[var(--ink)] text-[15px]"
                        >
                          <option value="">—</option>
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </FormField>
                    </div>
                    <FormField label="City or state (optional)" hint="e.g. Texas">
                      <TextInput maxLength={60} value={location} onChange={(e) => setLocation(e.target.value)} />
                    </FormField>
                    <p className="text-[12.5px] text-[var(--muted)] mb-3">{TESTIMONIAL_CONSENT_TEXT}</p>
                    <FormField
                      label="Type your full name to sign"
                      required
                      error={errors.signature}
                      showError={!!errors.signature}
                      hint={`Signed ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
                    >
                      <TextInput maxLength={100} value={signature} onChange={(e) => setSignature(e.target.value)} invalid={!!errors.signature} />
                    </FormField>
                  </div>
                )}
              </>
            )}

            {serverError && <div className="text-[13px] text-[var(--danger)] mb-3">{serverError}</div>}

            <div className="flex justify-between gap-3 mt-2">
              <Button variant="ghost" onClick={() => requestClose("notNow")} disabled={submitting}>
                Not now
              </Button>
              <Button onClick={submit} disabled={submitting}>
                {submitting ? "Sending…" : "Send feedback"}
              </Button>
            </div>
          </>
        )}
      </Panel>
    </div>,
    document.body
  );
}
